<main id="main-content" class="examples-page">
  <header class="examples-header">
    <div>
      <a routerLink="/design-system">Design system</a>
      <p class="ds-eyebrow">REFERENCE SCREENS</p>
      <h1>Product patterns in context</h1>
      <p>Ten implementation targets built entirely from the IQLabTest design system.</p>
    </div>
    <ds-badge tone="info">Internal documentation</ds-badge>
  </header>

  <div class="examples-layout">
    <nav aria-label="Reference screens">
      @for (screen of screens; track screen.id) {
        @if ($first || screens[$index - 1].group !== screen.group) {
          <strong>{{ screen.group }}</strong>
        }
        <button
          type="button"
          [class.active]="active() === screen.id"
          [attr.aria-current]="active() === screen.id ? 'page' : null"
          (click)="active.set(screen.id)"
        >
          {{ screen.label }}
        </button>
      }
    </nav>

    <section class="preview-region" [attr.aria-label]="active() + ' reference screen'">
      @switch (active()) {
        @case ('login') {
          <div class="screen-canvas screen-canvas--centered">
            <ds-card class="auth-card">
              <div class="product-lockup">
                <span aria-hidden="true">IQ</span><strong>IQLabTest</strong>
              </div>
              <p class="ds-eyebrow">SECURE ACCOUNT</p>
              <h2>Welcome back</h2>
              <p class="muted">Sign in to manage assessments and reports.</p>
              <form class="form-stack">
                <ds-field label="Email address" forId="example-login-email"
                  ><input
                    dsInput
                    id="example-login-email"
                    type="email"
                    autocomplete="email"
                    value="alex@example.com"
                /></ds-field>
                <ds-field label="Password" forId="example-login-password"
                  ><input
                    dsInput
                    id="example-login-password"
                    type="password"
                    autocomplete="current-password"
                    value="password"
                /></ds-field>
                <label class="check-row"
                  ><input dsCheckbox type="checkbox" /> Keep me signed in</label
                >
                <button dsButton size="large" type="button">Sign in</button>
              </form>
              <button dsButton variant="quiet" type="button">Need help signing in?</button>
            </ds-card>
          </div>
        }
        @case ('dashboard') {
          <div class="screen-canvas">
            <div class="screen-title">
              <div>
                <p class="ds-eyebrow">WORKSPACE OVERVIEW</p>
                <h2>Good morning, Alex</h2>
                <p>Monitor assessment activity and follow up where needed.</p>
              </div>
              <button dsButton type="button">Create assessment</button>
            </div>
            <div class="metric-grid">
              <ds-card
                ><span>Available credits</span><strong>248</strong
                ><small>Across 3 active campaigns</small></ds-card
              ><ds-card
                ><span>Completion rate</span><strong>84%</strong
                ><small>+6% from last month</small></ds-card
              ><ds-card
                ><span>Awaiting review</span><strong>12</strong
                ><small>4 completed today</small></ds-card
              >
            </div>
            <ds-card
              ><div class="section-heading">
                <div>
                  <h3>Recent assessments</h3>
                  <p>Latest candidate activity</p>
                </div>
                <button dsButton variant="quiet" type="button">View all</button>
              </div>
              <ds-table label="Recent assessment activity"
                ><thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Alex Morgan</td>
                    <td>Graduate intake</td>
                    <td><ds-badge tone="success">Complete</ds-badge></td>
                    <td>Today, 10:42</td>
                  </tr>
                  <tr>
                    <td>Sam Rivera</td>
                    <td>Product team</td>
                    <td><ds-badge tone="warning">In progress</ds-badge></td>
                    <td>—</td>
                  </tr>
                  <tr>
                    <td>Jordan Lee</td>
                    <td>Graduate intake</td>
                    <td><ds-badge>Invited</ds-badge></td>
                    <td>—</td>
                  </tr>
                </tbody></ds-table
              ></ds-card
            >
          </div>
        }
        @case ('instructions') {
          <div class="screen-canvas screen-canvas--centered">
            <ds-assessment-instructions
              title="Before you begin"
              summary="This assessment contains 40 questions across four reasoning domains. Choose the best answer for each question."
              [facts]="[
                { label: 'Questions', value: '40' },
                { label: 'Time', value: '35 minutes' },
                { label: 'Languages', value: '100+' },
              ]"
              ><ul>
                <li>Work in a quiet place without interruptions.</li>
                <li>You can move between questions before submitting.</li>
                <li>Your progress is saved automatically.</li>
              </ul>
              <button dsButton size="large" type="button">
                Begin assessment
              </button></ds-assessment-instructions
            >
          </div>
        }
        @case ('question') {
          <div class="screen-canvas assessment-canvas">
            <div class="assessment-topline">
              <ds-assessment-progress [answered]="11" [total]="40" /><ds-review-flag
                [flagged]="flagged()"
                (flaggedChange)="flagged.set($event)"
              />
            </div>
            <ds-question-card
              domain="Pattern reasoning"
              [position]="12"
              [total]="40"
              prompt="Which option completes the sequence?"
              ><div dsQuestionVisual class="example-visual" aria-label="Example abstract pattern">
                <span></span><span></span><span>?</span>
              </div>
              <ds-answer-option
                optionKey="A"
                label="Two circles followed by one square" /><ds-answer-option
                optionKey="B"
                label="One square followed by two circles"
                [selected]="true" /><ds-answer-option
                optionKey="C"
                label="Three alternating shapes" /><ds-answer-option
                optionKey="D"
                label="Two identical squares" /></ds-question-card
            ><ds-question-navigator [position]="12" [total]="40" />
          </div>
        }
        @case ('timed') {
          <div class="screen-canvas assessment-canvas">
            <div class="assessment-topline">
              <ds-assessment-progress [answered]="31" [total]="40" /><ds-assessment-timer
                [seconds]="238"
              />
            </div>
            <ds-alert tone="warning" title="Less than five minutes remain"
              ><p>
                Continue at a steady pace. Unanswered questions can be reviewed before submission.
              </p></ds-alert
            ><ds-question-card
              domain="Number series"
              [position]="32"
              [total]="40"
              prompt="Which number completes the series: 3, 6, 11, 18, 27, ?"
              ><ds-answer-option optionKey="A" label="34" /><ds-answer-option
                optionKey="B"
                label="36" /><ds-answer-option optionKey="C" label="38" /><ds-answer-option
                optionKey="D"
                label="40" /></ds-question-card
            ><ds-question-navigator [position]="32" [total]="40" />
          </div>
        }
        @case ('mobile') {
          <div class="screen-canvas screen-canvas--mobile">
            <div class="mobile-device">
              <div class="assessment-topline">
                <ds-assessment-progress [answered]="7" [total]="40" /><ds-assessment-timer
                  [seconds]="1734"
                />
              </div>
              <ds-question-card
                domain="Logical sequence"
                [position]="8"
                [total]="40"
                prompt="Which symbol comes next?"
                ><ds-answer-option optionKey="A" label="Circle" /><ds-answer-option
                  optionKey="B"
                  label="Square" /><ds-answer-option
                  optionKey="C"
                  label="Triangle" /><ds-answer-option
                  optionKey="D"
                  label="Diamond" /></ds-question-card
              ><ds-question-navigator [position]="8" [total]="40" />
            </div>
          </div>
        }
        @case ('results') {
          <div class="screen-canvas">
            <div class="screen-title">
              <div>
                <p class="ds-eyebrow">ASSESSMENT COMPLETE</p>
                <h2>Your indicative result</h2>
                <p>Review the estimate together with its confidence range and domain profile.</p>
              </div>
              <button dsButton variant="secondary" type="button">Download certificate</button>
            </div>
            <div class="results-grid">
              <ds-result-card
                title="Above average range"
                score="118"
                range="112–124"
                summary="Your result is an indicative estimate from this online assessment, not a clinical diagnosis or standardized credential."
                ><ds-alert title="Interpret with context"
                  ><p>
                    Language, environment, familiarity, and normal measurement error can affect
                    performance.
                  </p></ds-alert
                ></ds-result-card
              ><ds-card class="score-panel"
                ><ds-score-visualization
                  [value]="74"
                  displayValue="118"
                  label="Indicative estimate"
                />
                <div class="domain-list">
                  <div><span>Pattern reasoning</span><strong>9/10</strong></div>
                  <div><span>Logical sequences</span><strong>8/10</strong></div>
                  <div><span>Spatial reasoning</span><strong>7/10</strong></div>
                  <div><span>Quantitative reasoning</span><strong>8/10</strong></div>
                </div></ds-card
              >
            </div>
          </div>
        }
        @case ('report') {
          <div class="screen-canvas">
            <div class="screen-title">
              <div>
                <p class="ds-eyebrow">DETAILED REPORT</p>
                <h2>Alex Morgan</h2>
                <p>Completed 18 July 2026 · Verification ID 92af…e18b</p>
              </div>
              <div class="action-row">
                <button dsButton variant="secondary" type="button">Share verification</button
                ><button dsButton type="button">Download PDF</button>
              </div>
            </div>
            <div class="report-grid">
              <ds-card
                ><h3>Result overview</h3>
                <div class="report-score">
                  <ds-score-visualization [value]="74" displayValue="118" label="Estimate" />
                  <p>
                    The observed result falls in an above-average indicative range. The confidence
                    interval is 112–124.
                  </p>
                </div></ds-card
              ><ds-card
                ><h3>Important context</h3>
                <p class="muted">
                  This online assessment samples selected cognitive tasks. It is not suitable for
                  clinical, educational, or employment decisions.
                </p></ds-card
              >
            </div>
            <ds-card
              ><h3>Domain profile</h3>
              <ds-table label="Domain score details"
                ><thead>
                  <tr>
                    <th>Domain</th>
                    <th>Correct</th>
                    <th>Profile</th>
                    <th>Interpretation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Pattern reasoning</td>
                    <td>9/10</td>
                    <td><ds-badge tone="success">Relative strength</ds-badge></td>
                    <td>Strong rule detection</td>
                  </tr>
                  <tr>
                    <td>Logical sequences</td>
                    <td>8/10</td>
                    <td><ds-badge tone="info">Consistent</ds-badge></td>
                    <td>Accurate sequence inference</td>
                  </tr>
                  <tr>
                    <td>Spatial reasoning</td>
                    <td>7/10</td>
                    <td><ds-badge>Typical</ds-badge></td>
                    <td>Steady mental transformation</td>
                  </tr>
                </tbody></ds-table
              ></ds-card
            >
          </div>
        }
        @case ('history') {
          <div class="screen-canvas">
            <div class="screen-title">
              <div>
                <p class="ds-eyebrow">ACCOUNT</p>
                <h2>Assessment history</h2>
                <p>Access completed assessments, reports, and verification records.</p>
              </div>
              <button dsButton type="button">Start new assessment</button>
            </div>
            <ds-card
              ><div class="filter-row">
                <ds-field label="Search" forId="history-search"
                  ><input
                    dsInput
                    id="history-search"
                    type="search"
                    placeholder="Search assessments" /></ds-field
                ><ds-field label="Status" forId="history-status"
                  ><select dsSelect id="history-status">
                    <option>All statuses</option>
                    <option>Complete</option>
                    <option>Archived</option>
                  </select></ds-field
                >
              </div>
              <ds-assessment-history [items]="history"
            /></ds-card>
          </div>
        }
        @case ('settings') {
          <div class="screen-canvas">
            <div class="screen-title">
              <div>
                <p class="ds-eyebrow">ACCOUNT</p>
                <h2>Settings</h2>
                <p>Manage profile, preferences, privacy, and security.</p>
              </div>
            </div>
            <div class="settings-grid">
              <nav aria-label="Settings sections">
                <button class="active" type="button">Profile</button
                ><button type="button">Preferences</button><button type="button">Privacy</button
                ><button type="button">Security</button>
              </nav>
              <ds-card
                ><h3>Profile information</h3>
                <form class="form-stack">
                  <div class="form-grid">
                    <ds-field label="First name" forId="settings-first"
                      ><input dsInput id="settings-first" value="Alex" /></ds-field
                    ><ds-field label="Last name" forId="settings-last"
                      ><input dsInput id="settings-last" value="Morgan"
                    /></ds-field>
                  </div>
                  <ds-field
                    label="Email address"
                    forId="settings-email"
                    hint="Changing your email requires verification."
                    ><input
                      dsInput
                      id="settings-email"
                      type="email"
                      value="alex@example.com" /></ds-field
                  ><ds-field label="Language" forId="settings-language"
                    ><select dsSelect id="settings-language">
                      <option>English</option>
                      <option>Nederlands</option>
                    </select></ds-field
                  ><label class="check-row"
                    ><input dsCheckbox type="checkbox" checked /> Email me when a new report is
                    available</label
                  >
                  <div class="action-row">
                    <button dsButton type="button">Save changes</button
                    ><button dsButton variant="secondary" type="button">Cancel</button>
                  </div>
                </form></ds-card
              >
            </div>
          </div>
        }
      }
    </section>
  </div>
</main>
